import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Newspaper, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// API URL - uses localhost:8001 for development, otherwise uses same origin as page
const getApiUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:8001/api';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8001/api';
    }
    return `${window.location.origin}/api`;
};
const API = getApiUrl();

function ConfirmSubscription() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [alreadyVerified, setAlreadyVerified] = useState(false);

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setStatus('error');
            setMessage('Invalid verification link');
            return;
        }

        // Verify the email
        const verifyEmail = async () => {
            try {
                const response = await axios.get(`${API}/verify-email/${token}`);
                setStatus('success');
                setMessage(response.data.message);
                setEmail(response.data.email);
                setAlreadyVerified(response.data.already_verified);
                try {
                    localStorage.setItem('tnf_subscriber_token', token);
                    localStorage.setItem('tnf_subscribed', 'true');
                } catch (e) {
                    console.warn('Could not save subscriber token to localStorage:', e);
                }
            } catch (error) {
                setStatus('error');
                setMessage(error.response?.data?.detail || 'Verification failed. Please try again or contact support.');
            }
        };

        verifyEmail();
    }, [searchParams]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <img
                        src="/logo192.png"
                        alt="TNF"
                        className="w-16 h-16 rounded-xl object-cover border border-emerald-500/25 shadow-md mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-black tracking-tight mb-1">THE NEURAL FEED</h1>
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                        AI Intelligence • Daily Insights
                    </p>
                </div>

                {/* Status Card */}
                <Card className="border-2">
                    <CardContent className="p-8 text-center">
                        {status === 'loading' && (
                            <>
                                <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
                                <h2 className="text-xl font-bold mb-2">Verifying your email...</h2>
                                <p className="text-sm text-muted-foreground">
                                    Please wait while we confirm your subscription
                                </p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <CheckCircle2 className="w-12 h-12 mx-auto text-green-600 mb-4" />
                                <h2 className="text-xl font-bold mb-2">
                                    {alreadyVerified ? 'Already Verified!' : 'Email Verified!'}
                                </h2>
                                {email && (
                                    <p className="text-sm text-muted-foreground mb-3">
                                        {email}
                                    </p>
                                )}
                                <p className="text-base text-foreground mb-6">
                                    {message}
                                </p>
                                <div className="space-y-3">
                                    <Button
                                        onClick={() => navigate('/')}
                                        className="w-full rounded-full"
                                    >
                                        Go to Dashboard
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        You'll receive our next newsletter with the latest AI insights
                                    </p>
                                </div>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <XCircle className="w-12 h-12 mx-auto text-red-600 mb-4" />
                                <h2 className="text-xl font-bold mb-2">Verification Failed</h2>
                                <p className="text-sm text-muted-foreground mb-6">
                                    {message}
                                </p>
                                <div className="space-y-2">
                                    <Button
                                        onClick={() => navigate('/')}
                                        variant="outline"
                                        className="w-full rounded-full"
                                    >
                                        Return to Homepage
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        Need help? The link may have expired or been used already
                                    </p>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground mt-6">
                    172+ AI Sources • Daily Updates
                </p>
            </div>
        </div>
    );
}

export default ConfirmSubscription;
